import React, { type FC } from 'react';
import {
  formatDate,
  formatFloat,
  getPropertyAddress,
} from '../../utility/utility_functions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import {
  abandonFocus,
  maintainFocus,
  setFocusedIndex,
} from '../../redux/state/avmMapSlice';

type SoldAvmCompsTableProps = {
  comps: any;
};

const tdStyling = 'p-2 text-xs';
const compsHeaders = [
  'MLS #',
  'Sold',
  'Address',
  'Sold Price',
  '$/ Sq Ft',
  'Sq Ft',
  'Beds/ Baths',
  'Year Build',
  'Stories',
  'CDOM',
  '',
];

export const SoldAvmCompsTable: FC<SoldAvmCompsTableProps> = ({ comps }) => {
  const isActive = false;
  const dispatch = useDispatch<AppDispatch>();
  const focusedIndex = useSelector(
    (state: RootState) => state.avmMap.focusedIndex
  );
  const keepFocus = useSelector(
    (state: RootState) => state.avmMap.maintainFocus
  );

  return (
    <table
      id="sold-avm-comps-table"
      className="table-auto divide-y divide-gray-300 rounded-xl w-full"
    >
      <thead className="rounded-xl">
        <tr className="sticky bg-indigo-950 text-white text-center rounded-xl">
          {compsHeaders.map((h) => (
            <th key={h} className="p-2 text-sm text-bold text-wrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      {comps?.map((comp, index) => {
        const linkToProperty = (text: string) => {
          const propertyRoute = `/properties/${comp?.property?.id}`;
          return (
            <a
              href={propertyRoute}
              rel="noopener noreferrer"
              target="_blank"
              className="text-indigo-950 hover:text-indigo-900"
            >
              {text}
            </a>
          );
        };

        const getBedAndBaths = (): string => {
          const beds = comp?.property?.last_sold_listing?.bedrooms ?? 'N/A';

          const fBaths = comp?.property?.last_sold_listing?.full_bathrooms;
          const hBaths = comp?.property?.last_sold_listing?.half_bathrooms;

          let baths;
          if (!fBaths && !hBaths) {
            baths = 'N/A';
          } else if (!fBaths) {
            baths = `${0.5 * hBaths}`;
          } else if (!hBaths) {
            baths = `${fBaths}`;
          } else {
            const convertedHalfBaths = 0.5 * hBaths;
            baths = `${fBaths + convertedHalfBaths}`;
          }

          return `${beds} / ${baths}`;
        };

        const getSaleType = (): string => {
          const howSold = comp?.property?.last_sold_listing?.how_sold;
          if (!howSold) return 'N/A';
          return howSold === '_1SLLR' ? 'Owner Financed' : howSold;
        };

        const clearEmphasis = () => {
          if (!keepFocus) {
            dispatch(setFocusedIndex(null));
          }
        };

        const setEmphasisHover = () => {
          if (!keepFocus) {
            dispatch(setFocusedIndex({ index, isActive }));
          }
        };

        const setEmphasisClick = () => {
          if (!keepFocus) {
            dispatch(maintainFocus());
          } else if (
            index === focusedIndex?.index &&
            focusedIndex?.isActive === isActive
          ) {
            dispatch(abandonFocus());
          } else {
            dispatch(setFocusedIndex({ index, isActive }));
          }
        };

        const getSoldPricePerSqft = (comp) => {
          const price = comp?.property?.last_sold_listing?.close_price;
          const sqft = comp?.property?.sqft;
          if (price == null || sqft == null || sqft == 0) {
            return null;
          }

          return `$${formatFloat(price / sqft, 2)}`;
        };

        return (
          <tbody key={comp.id}>
            <tr
              className={`${
                index === focusedIndex?.index &&
                focusedIndex?.isActive === isActive
                  ? 'bg-indigo-200'
                  : ''
              } text-xs text-center whitespace-nowrap border`}
              onMouseEnter={setEmphasisHover}
              onMouseLeave={clearEmphasis}
              onClick={setEmphasisClick}
            >
              <td className={tdStyling}>
                {comp?.property?.last_mls_id ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {formatDate(comp?.property?.last_sold_listing?.close_date) ??
                  'N/A'}
              </td>
              <td className={tdStyling}>
                {linkToProperty(getPropertyAddress(comp?.property))}
              </td>
              <td className={tdStyling}>
                {comp?.property?.last_sold_listing?.close_price
                  ? `${'$'}` +
                    formatFloat(
                      comp?.property?.last_sold_listing?.close_price,
                      0
                    )
                  : 'N/A'}
              </td>
              <td className={tdStyling}>
                {getSoldPricePerSqft(comp) ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {formatFloat(comp?.property?.sqft, 0) ?? 'N/A'}
              </td>
              <td className={tdStyling}>{getBedAndBaths()}</td>
              <td className={tdStyling}>
                {comp?.property?.last_sold_listing?.year_built ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {comp?.property?.last_sold_listing?.stories ?? 'N/A'}
              </td>
              <td className={tdStyling}>
                {comp?.property?.last_sold_listing?.cumulative_days_on_market ??
                  'N/A'}
              </td>
              <td className={tdStyling}>{getSaleType() ?? 'N/A'}</td>
            </tr>
          </tbody>
        );
      })}
    </table>
  );
};

export default SoldAvmCompsTable;
